import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Table, Form, Button, Badge, Spinner, InputGroup, Alert } from 'react-bootstrap';
import api from '../../config/roundrobinApi';

export default function AgentsTab() {
  const [agents, setAgents] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState({});
  const [message, setMessage] = useState(null);

  const fetchAgents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/agents');
      setAgents(res.data);
      setChanges({});
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAgents(); }, [fetchAgents]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/agents/import');
      setMessage({ type: 'success', text: `Imported ${res.data.count} agents` });
      fetchAgents();
    } catch (err) { setMessage({ type: 'danger', text: err.response?.data?.detail || 'Import failed' }); }
    finally { setImporting(false); }
  };

  const handleChange = (zendesk_id, field, value) => {
    setChanges(prev => ({
      ...prev,
      [zendesk_id]: { ...prev[zendesk_id], zendesk_id, [field]: value }
    }));
  };

  const handleSave = async () => {
    const updates = Object.values(changes);
    if (!updates.length) return;
    setSaving(true);
    try {
      await api.put('/agents/bulk-update', { agents: updates });
      setMessage({ type: 'success', text: 'Saved successfully' });
      fetchAgents();
    } catch (err) { setMessage({ type: 'danger', text: 'Save failed' }); }
    finally { setSaving(false); }
  };

  const getVal = (agent, field) => {
    const c = changes[agent.zendesk_id];
    if (c && c[field] !== undefined) return c[field];
    return agent[field];
  };

  const allGroups = useMemo(() => {
    const groupMap = {};
    agents.forEach(a => (a.groups || []).forEach(g => {
      const name = typeof g === 'object' ? g.name : g;
      if (name) groupMap[name] = true;
    }));
    return Object.keys(groupMap).sort();
  }, [agents]);

  const filtered = agents.filter(a => {
    if (search) {
      const s = search.toLowerCase();
      if (!(a.name || '').toLowerCase().includes(s) && !(a.email || '').toLowerCase().includes(s)) return false;
    }
    if (selectedGroup) {
      const agentGroups = (a.groups || []).map(g => typeof g === 'object' ? g.name : g);
      if (!agentGroups.includes(selectedGroup)) return false;
    }
    return true;
  });

  return (
    <>
      {message && <Alert variant={message.type} dismissible onClose={() => setMessage(null)}>{message.text}</Alert>}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2">
          <InputGroup style={{ maxWidth: '250px' }}>
            <Form.Control placeholder="Search agents..." value={search} onChange={e => setSearch(e.target.value)} />
          </InputGroup>
          <Form.Select style={{ maxWidth: '200px' }} value={selectedGroup} onChange={e => setSelectedGroup(e.target.value)}>
            <option value="">All Groups</option>
            {allGroups.map(g => <option key={g} value={g}>{g}</option>)}
          </Form.Select>
        </div>
        <div>
          <Button variant="outline-primary" className="me-2" onClick={handleImport} disabled={importing}>
            {importing ? <><Spinner size="sm" className="me-1" />Importing...</> : 'Import Agents'}
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={saving || !Object.keys(changes).length}>
            {saving ? <><Spinner size="sm" className="me-1" />Saving...</> : 'Save'}
          </Button>
        </div>
      </div>

      {loading ? <div className="text-center py-5"><Spinner /></div> : (
        <Table hover responsive size="sm">
          <thead className="table-light">
            <tr>
              <th>Active</th>
              <th>Zendesk ID</th>
              <th>Name</th>
              <th>Email</th>
              <th>Groups</th>
              <th>Tags</th>
              <th>Availability</th>
              <th style={{width:'100px'}}>Limit</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(agent => {
              const isDeleted = getVal(agent, 'delete');
              return (
                <tr key={agent.zendesk_id} className={isDeleted ? 'text-decoration-line-through text-muted' : ''}>
                  <td><Form.Check type="switch" checked={getVal(agent, 'active')} onChange={e => handleChange(agent.zendesk_id, 'active', e.target.checked)} /></td>
                  <td><small>{agent.zendesk_id}</small></td>
                  <td>{agent.name}</td>
                  <td><small>{agent.email}</small></td>
                  <td>{(agent.groups || []).map((g, i) => <Badge key={i} bg="info" className="me-1">{typeof g === 'object' ? g.name : g}</Badge>)}</td>
                  <td>{(agent.tags || []).map((t, i) => <Badge key={i} bg="secondary" className="me-1">{t}</Badge>)}</td>
                  <td><Badge bg={agent.availability === 'available' ? 'success' : 'warning'}>{agent.availability}</Badge></td>
                  <td><Form.Control type="number" size="sm" min={0} value={getVal(agent, 'agent_limit') ?? ''} onChange={e => handleChange(agent.zendesk_id, 'agent_limit', e.target.value ? parseInt(e.target.value) : null)} /></td>
                  <td><Button variant="outline-danger" size="sm" onClick={() => handleChange(agent.zendesk_id, 'delete', !isDeleted)}>&#x2715;</Button></td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
}
